import { useParams } from "react-router-dom";
import BannerComp from "../components/BannerComp";
import RelatedPost from "../components/RelatedPost";
import Seo from "../components/Seo";
import NotFound from "../NotFound";
import { caseStudies, getCaseStudyBySlug } from "../data/caseStudies";
import { SITE_LOGO, SITE_NAME, buildCanonicalUrl } from "../lib/seo";
import styles from "./CaseStudyDetail.module.css";

function RichText({ html, className = "" }) {
  if (!html) {
    return null;
  }

  return (
    <div
      className={`${styles.richText} ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const caseStudy = getCaseStudyBySlug(slug);

  if (!caseStudy) {
    return <NotFound />;
  }

  const relatedPosts = caseStudies
    .filter((post) => post.slug !== caseStudy.slug)
    .slice(0, 4);
  const canonicalPath = `/case-studies/${caseStudy.slug}`;
  const canonicalUrl = buildCanonicalUrl(canonicalPath);
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CaseStudy",
      headline: caseStudy.title,
      description: caseStudy.excerpt,
      image: caseStudy.image.startsWith("http")
        ? caseStudy.image
        : `https://psprcm.us${caseStudy.image}`,
      datePublished: caseStudy.date,
      dateModified: caseStudy.date,
      mainEntityOfPage: canonicalUrl,
      author: {
        "@type": "Organization",
        name: SITE_NAME,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: SITE_LOGO,
        },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://psprcm.us/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Case Studies",
          item: buildCanonicalUrl("/case-studies"),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: caseStudy.title,
          item: canonicalUrl,
        },
      ],
    },
  ];

  return (
    <>
      <Seo
        title={`${caseStudy.title} | PSP RCM Case Studies`}
        metaTitle={`${caseStudy.title} | PSP RCM Case Studies`}
        description={caseStudy.excerpt}
        // keywords={caseStudy.metaKeywords}
        canonicalPath={canonicalPath}
        schema={schema}
        image={caseStudy.image}
        type="article"
      />
      <BannerComp
        heading={caseStudy.title}
        paragraph={caseStudy.excerpt}
        BannerBgImg="/pages-banner.webp"
        centered
      />

      <section className={styles.section}>
        <div className={`container ${styles.layout}`}>
          <article className={styles.article}>
            <img
              src={caseStudy.image}
              alt={caseStudy.title}
              className={styles.coverImage}
            />

            <div className={styles.body}>
              {caseStudy.sections.map((section) => (
                <section
                  key={
                    section.heading ?? section.paragraphs?.[0] ?? caseStudy.slug
                  }
                  className={styles.contentBlock}
                >
                  {section.heading ? <h2>{section.heading}</h2> : null}
                  {section.paragraphs.map((paragraph) => (
                    <RichText key={paragraph} html={paragraph} />
                  ))}
                </section>
              ))}
            </div>
          </article>

          <div className={styles.sidebar}>
            <RelatedPost
              posts={relatedPosts}
              basePath="/case-studies"
              eyebrow="More Case Studies"
              title="Related Case Studies"
            />
          </div>
        </div>
      </section>
    </>
  );
}
